// Locates AI-proposed edits in the document text and turns approved ones into a
// Quill change-delta. Every edit is given a concrete landing spot so it can be
// shown inline in the diff exactly where it will end up.

function normalize(s) {
  return (s || '').replace(/\s+/g, ' ').trim();
}

// Common abbreviations the model may use for document headings.
const SECTION_ALIASES = {
  HPI: 'HISTORY OF PRESENT ILLNESS',
  PMH: 'PAST MEDICAL HISTORY',
  PSH: 'PAST SURGICAL HISTORY',
  ROS: 'REVIEW OF SYSTEMS',
  FH: 'FAMILY HISTORY',
  SH: 'SOCIAL HISTORY',
  'A&P': 'ASSESSMENT & PLAN',
  VITALS: 'VITALS',
  'SLEEP ROS': 'SLEEP ROS',
  'SLEEP REVIEW OF SYSTEMS': 'SLEEP ROS',
  'SLEEP REVIEW SYSTEMS': 'SLEEP ROS',
  RHM: 'ROUTINE HEALTH MAINTENANCE',
  'ROUTING HEALTH MAINTENANCE': 'ROUTINE HEALTH MAINTENANCE',
};

const RHM_ROUTE_TYPES = new Set(['NP', 'NS', 'FFP', 'FFS', 'STUDENT', 'MEDSTUD']);

const MONTHS = {
  jan: 1, january: 1,
  feb: 2, february: 2,
  mar: 3, march: 3,
  apr: 4, april: 4,
  may: 5,
  jun: 6, june: 6,
  jul: 7, july: 7,
  aug: 8, august: 8,
  sep: 9, sept: 9, september: 9,
  oct: 10, october: 10,
  nov: 11, november: 11,
  dec: 12, december: 12,
};

function pad2(n) {
  return String(n).padStart(2, '0');
}

function fullYear(y) {
  if (y === null || y === undefined || y === '') return null;
  const n = Number(y);
  if (!Number.isFinite(n)) return null;
  if (n < 100) return n >= 70 ? 1900 + n : 2000 + n;
  return n;
}

function dateParts(year, month, day) {
  const m = Number(month);
  const d = Number(day);
  const y = fullYear(year);
  if (!Number.isFinite(m) || !Number.isFinite(d) || m < 1 || m > 12 || d < 1 || d > 31) {
    return null;
  }
  return {
    year: y,
    month: m,
    day: d,
    key: `${y || 'any'}-${pad2(m)}-${pad2(d)}`,
    display: `${m}/${d}${y ? `/${y}` : ''}`,
  };
}

function cleanDateLine(line) {
  return String(line || '')
    .trim()
    .replace(/^#+\s*/, '')
    .replace(/^\*\*(.*)\*\*$/, '$1')
    .trim();
}

function parseDateAtStart(line) {
  const t = cleanDateLine(line);
  let m = t.match(/^(?:date|visit|session|note)?\s*[:#-]?\s*(\d{4})-(\d{1,2})-(\d{1,2})(?!\d)/i);
  if (m) return dateParts(m[1], m[2], m[3]);

  m = t.match(/^(?:date|visit|session|note)?\s*[:#-]?\s*(\d{1,2})[\/.-](\d{1,2})(?:[\/.-](\d{2,4}))?\b/i);
  if (m) return dateParts(m[3], m[1], m[2]);

  m = t.match(/^(?:date|visit|session|note)?\s*[:#-]?\s*([A-Za-z]+)\.?\s+(\d{1,2})(?:,?\s+(\d{2,4}))?\b/);
  if (m) {
    const month = MONTHS[m[1].toLowerCase()];
    if (month) return dateParts(m[3], month, m[2]);
  }
  return null;
}

function parseSourceDate(value) {
  if (!value) return null;
  const s = String(value).trim();
  let m = s.match(/\b(\d{4})-(\d{1,2})-(\d{1,2})(?!\d)/);
  if (m) return dateParts(m[1], m[2], m[3]);
  m = s.match(/\b(\d{1,2})[\/.-](\d{1,2})(?:[\/.-](\d{2,4}))?\b/);
  if (m) return dateParts(m[3], m[1], m[2]);
  return parseDateAtStart(s);
}

function datesMatch(sourceDate, sectionDate) {
  if (!sourceDate || !sectionDate) return false;
  if (sourceDate.month !== sectionDate.month || sourceDate.day !== sectionDate.day) return false;
  if (sourceDate.year && sectionDate.year) return sourceDate.year === sectionDate.year;
  return true;
}

function isDateHeadingLine(line) {
  const t = cleanDateLine(line);
  if (!t || t.length > 90) return false;
  return !!parseDateAtStart(t);
}

function isServiceTimeLine(line) {
  const t = String(line || '').trim();
  return /^I spent\s*(?:\(\d+\)|\d+)\s*minutes\b/i.test(t);
}

// Section headings in these docs are usually short uppercase lines. Also accept
// compact colon headings such as "HPI: cough." so empty/non-template docs work.
function isHeadingLine(line) {
  const t = line.trim();
  if (!t) return false;
  const upper = t.toUpperCase();
  const colon = upper.match(/^([A-Z0-9 &/().,'-]{2,24}):($|\s+)/);
  if (colon && (SECTION_ALIASES[colon[1]] || colon[1] === 'RHM')) return true;
  return t.length >= 3 && /[A-Z]/.test(t) && /^[A-Z0-9 &/().,:'-]+$/.test(t);
}

function lineInfo(text) {
  const lines = String(text || '').split('\n');
  const offsets = [];
  let pos = 0;
  for (const line of lines) {
    offsets.push(pos);
    pos += line.length + 1;
  }
  return { lines, offsets };
}

function getDateSections(text) {
  const { lines, offsets } = lineInfo(text);
  const heads = [];
  for (let i = 0; i < lines.length; i += 1) {
    const date = parseDateAtStart(lines[i]);
    if (date && isDateHeadingLine(lines[i])) heads.push({ line: i, date });
  }
  return heads.map((h, idx) => {
    const next = heads[idx + 1];
    const endLine = next ? next.line : lines.length;
    let lastContent = h.line;
    for (let i = h.line + 1; i < endLine; i += 1) {
      if (lines[i].trim()) lastContent = i;
    }
    return {
      ...h,
      start: offsets[h.line],
      end: next ? offsets[next.line] : text.length,
      insertEnd: offsets[lastContent] + lines[lastContent].length,
    };
  });
}

function getUndatedRanges(text, dateSections) {
  if (!dateSections.length) return [{ start: 0, end: text.length }];
  const ranges = [];
  let cursor = 0;
  for (const section of dateSections) {
    if (cursor < section.start) ranges.push({ start: cursor, end: section.start });
    cursor = Math.max(cursor, section.end);
  }
  if (cursor < text.length) ranges.push({ start: cursor, end: text.length });
  return ranges;
}

function inRanges(index, ranges) {
  return (ranges || []).some((r) => index >= r.start && index < r.end);
}

function dateSectionAtIndex(dateSections, index) {
  return dateSections.find((s) => index >= s.start && index < s.end) || null;
}

function normalizeSectionName(section) {
  if (!section) return '';
  let target = cleanDateLine(section)
    .toUpperCase()
    .replace(/\s*-\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/:$/, '')
    .trim();
  if (SECTION_ALIASES[target]) target = SECTION_ALIASES[target];
  return target;
}

function sectionDisplay(section) {
  const target = normalizeSectionName(section);
  if (!target) return '';
  return target === 'RHM' ? 'RHM' : target;
}

function isRhmSectionName(section) {
  return normalizeSectionName(section) === 'ROUTINE HEALTH MAINTENANCE';
}

function isBareDateLine(line) {
  const t = cleanDateLine(line);
  return /^(?:date|visit|session|note)?\s*[:#-]?\s*(?:\d{4}-\d{1,2}-\d{1,2}|\d{1,2}[\/.-]\d{1,2}(?:[\/.-]\d{2,4})?|[A-Za-z]+\.?\s+\d{1,2}(?:,?\s+\d{2,4})?)\s*$/i.test(t);
}

function stripSectionHeadingPrefix(line, section) {
  const target = normalizeSectionName(section);
  if (!target) return null;
  const trimmed = String(line || '').trim();
  const normalized = normalizeSectionName(trimmed);
  if (normalized === target) return '';

  const colon = trimmed.match(/^([^:]{2,80}):\s*(.*)$/);
  if (colon && normalizeSectionName(colon[1]) === target) {
    return colon[2].trim();
  }
  return null;
}

function stripLeadingPlacementContext(text, sourceDate, section) {
  let out = String(text || '').trim();
  if (!out) return out;

  const lines = out.split('\n');
  if (lines.length > 1 && isBareDateLine(lines[0])) {
    out = lines.slice(1).join('\n').trim();
  } else if (sourceDate) {
    const firstLine = lines[0] || '';
    const datePrefix = firstLine.match(/^\s*(?:\d{4}-\d{1,2}-\d{1,2}|\d{1,2}[\/.-]\d{1,2}(?:[\/.-]\d{2,4})?)\s+(.+)$/);
    if (datePrefix) {
      const stripped = stripSectionHeadingPrefix(datePrefix[1], section);
      if (stripped !== null) {
        lines[0] = stripped;
        out = lines.join('\n').trim();
      }
    }
  }

  const nextLines = out.split('\n');
  const strippedHeading = stripSectionHeadingPrefix(nextLines[0], section);
  if (strippedHeading !== null) {
    nextLines[0] = strippedHeading;
    out = nextLines.join('\n').trim();
  }
  return out;
}

function leadingPlacementSection(text, sourceDate) {
  let out = String(text || '').trim();
  if (!out) return '';

  let lines = out.split('\n');
  if (lines.length > 1 && isBareDateLine(lines[0])) {
    lines = lines.slice(1);
  } else if (sourceDate) {
    const firstLine = lines[0] || '';
    const datePrefix = firstLine.match(/^\s*(?:\d{4}-\d{1,2}-\d{1,2}|\d{1,2}[\/.-]\d{1,2}(?:[\/.-]\d{2,4})?)\s+(.+)$/);
    if (datePrefix) lines[0] = datePrefix[1].trim();
  }

  const first = cleanDateLine(lines[0] || '');
  if (!first || !isHeadingLine(first)) return '';
  const colon = first.match(/^([^:]{2,80}):/);
  return normalizeSectionName(colon ? colon[1] : first);
}

function stripLeadingRhmHeading(text) {
  return String(text || '')
    .replace(/^\s*(?:ROUTINE HEALTH MAINTENANCE|ROUTING HEALTH MAINTENANCE|RHM)\s*[:\n]+/i, '')
    .trim();
}

function looksLikeActionItemLine(line) {
  const t = String(line || '')
    .replace(/^\s*(?:#|[-*•]|\d+[.)])\s*/, '')
    .trim()
    .toLowerCase();
  if (!t) return false;
  // Dated plan lines (e.g. "6/17 Plan: Order...") are visit-summary entries,
  // not standalone action items — skip them so they aren't wrapped in # prefix.
  if (/^\d{1,2}\/\d{1,2}/.test(t)) return false;
  return (
    /\b(order|refer|referral|schedule|return|recheck|repeat|follow\s*-?\s*up|f\/u|obtain|get|send|start|stop|increase|decrease|continue|monitor|call|book|arrange|prescribe|refill)\b/.test(t)
    || /\b(labs?|imaging|x-?ray|ct|mri|ultrasound|echo|ecg|ekg|a1c|lipid|panel|cardiology|pulmonary|sleep study|medication|rx)\b/.test(t)
  );
}

function formatRhmActionItems(text) {
  const raw = stripLeadingRhmHeading(String(text || ''));
  if (!raw) return raw;

  const lines = raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const out = [];
  for (const line of lines) {
    if (/^(plan|orders?|follow-?ups?|action items?)\s*:/i.test(line)) continue;
    const stripped = line.replace(/^\s*(?:#|[-*•]|\d+[.)])\s*/, '').trim();
    if (!stripped) continue;
    out.push(looksLikeActionItemLine(stripped) ? `# ${stripped}` : stripped);
  }
  return out.join('\n').trim();
}

// Locate `needle` in `text`: exact indexOf first, then a whitespace-normalized
// fallback. `acceptIndex` lets date-aware placement reject wrong-date matches.
function findIndex(text, needle, acceptIndex = () => true) {
  if (!needle) return -1;
  let exact = text.indexOf(needle);
  while (exact !== -1) {
    if (acceptIndex(exact)) return exact;
    exact = text.indexOf(needle, exact + 1);
  }

  const nText = normalize(text);
  const nNeedle = normalize(needle);
  const nIdx = nText.indexOf(nNeedle);
  if (nIdx === -1) return -1;

  const wordsBefore = nText.slice(0, nIdx).split(' ').filter(Boolean).length;
  let seen = 0;
  const matches = text.match(/\S+/g) || [];
  let raw = 0;
  for (const w of matches) {
    if (seen === wordsBefore) {
      raw = text.indexOf(w, raw);
      break;
    }
    raw = text.indexOf(w, raw) + w.length;
    seen += 1;
  }
  return acceptIndex(raw) ? raw : -1;
}

// Index at the end of the named section's content. Optional ranges restrict the
// search to same-date or undated document regions.
function findSectionInsert(text, section, ranges = null) {
  const target = normalizeSectionName(section);
  if (!target) return -1;
  const targetIsRhm = target === 'ROUTINE HEALTH MAINTENANCE';
  const { lines, offsets } = lineInfo(text);
  const allowed = (i) => !ranges || inRanges(offsets[i], ranges);

  let hi = -1;
  for (let i = 0; i < lines.length; i += 1) {
    if (!allowed(i)) continue;
    const t = normalizeSectionName(lines[i]);
    if (!t) continue;
    if (t === target || t.startsWith(`${target} `) || target.startsWith(t)) {
      hi = i;
      break;
    }
  }
  if (hi === -1) {
    for (let i = 0; i < lines.length; i += 1) {
      if (!allowed(i)) continue;
      if (normalizeSectionName(lines[i]).includes(target)) {
        hi = i;
        break;
      }
    }
  }
  if (hi === -1) return -1;

  const rangeEnd = ranges?.find((r) => offsets[hi] >= r.start && offsets[hi] < r.end)?.end ?? text.length;
  let nextHi = lines.length;
  for (let i = hi + 1; i < lines.length; i += 1) {
    if (
      offsets[i] >= rangeEnd
      || isDateHeadingLine(lines[i])
      || (targetIsRhm && isServiceTimeLine(lines[i]))
      || isHeadingLine(lines[i])
    ) {
      nextHi = i;
      break;
    }
  }
  let lastContent = hi;
  for (let i = hi + 1; i < nextHi; i += 1) {
    if (lines[i].trim()) lastContent = i;
  }
  return offsets[lastContent] + lines[lastContent].length;
}

function findSectionStart(text, section, ranges = null) {
  const target = normalizeSectionName(section);
  if (!target) return -1;
  const { lines, offsets } = lineInfo(text);
  const allowed = (i) => !ranges || inRanges(offsets[i], ranges);

  for (let i = 0; i < lines.length; i += 1) {
    if (!allowed(i)) continue;
    const t = normalizeSectionName(lines[i]);
    if (!t) continue;
    if (t === target || t.startsWith(`${target} `) || target.startsWith(t)) {
      return offsets[i];
    }
  }
  for (let i = 0; i < lines.length; i += 1) {
    if (!allowed(i)) continue;
    if (normalizeSectionName(lines[i]).includes(target)) return offsets[i];
  }
  return -1;
}

function chooseTargetSection(text, declaredSection, leadingSection, undatedRanges) {
  if (!leadingSection) return declaredSection;
  const leadingExists = findSectionInsert(text, leadingSection, undatedRanges) !== -1
    || findSectionInsert(text, leadingSection) !== -1;
  if (!leadingExists) return declaredSection;

  const declared = normalizeSectionName(declaredSection);
  const declaredExists = declared && (
    findSectionInsert(text, declared, undatedRanges) !== -1
    || findSectionInsert(text, declared) !== -1
  );
  if (!declaredExists || declared !== leadingSection) return leadingSection;
  return declaredSection;
}

function shouldRouteToRhm(edit, documentType) {
  if (edit.kind !== 'addition') return false;
  if (!RHM_ROUTE_TYPES.has(String(documentType || '').toUpperCase().trim())) return false;
  const section = String(edit.section || '').toLowerCase();
  const text = `${section} ${edit.newText || ''} ${edit.rationale || ''}`.toLowerCase();
  return (
    isRhmSectionName(edit.section)
    || /\brhm\b/.test(section)
    || /\b(orders?|follow\s*-?\s*ups?|f\/u|action items?)\b/.test(section)
    || /\b(orders?|ordered|ordering|follow\s*-?\s*up|f\/u|labs?|imaging|x-?ray|ct|mri|referr?al|prescrib|rx|schedule|return|rtc|recheck|repeat|obtain|monitor|call|book|arrange)\b/.test(text)
  );
}

function findCanonicalRhmInsert(text, undatedRanges) {
  const undatedIdx = findSectionInsert(text, 'RHM', undatedRanges);
  if (undatedIdx !== -1) return undatedIdx;
  return findSectionInsert(text, 'RHM');
}

function appendInsert(text, newText) {
  return text.trim() ? `\n${newText}` : newText;
}

function createSectionInsert(text, section, newText) {
  const label = sectionDisplay(section);
  if (!label) return appendInsert(text, newText);
  const prefix = text.trim() ? (text.endsWith('\n') ? '\n' : '\n\n') : '';
  return `${prefix}${label}\n${newText}`;
}

function insertionPrefix(text, index) {
  if (!text.trim() || index <= 0) return '';
  const before = text.slice(0, index);
  if (!before.trim() || before.endsWith('\n\n')) return '';
  return before.endsWith('\n') ? '\n' : '\n\n';
}

function insertionSuffix(text, index) {
  if (index >= text.length) return '';
  const after = text.slice(index);
  if (!after.trim() || after.startsWith('\n\n')) return '';
  return after.startsWith('\n') ? '\n' : '\n\n';
}

function findDateSectionCreateIndex(text, undatedRanges) {
  const vitalsIdx = findSectionStart(text, 'VITALS', undatedRanges);
  if (vitalsIdx !== -1) {
    const hpiIdx = findSectionStart(text, 'HISTORY OF PRESENT ILLNESS', undatedRanges);
    if (hpiIdx === -1 || hpiIdx < vitalsIdx) return vitalsIdx;
  }

  const hpiEnd = findSectionInsert(text, 'HISTORY OF PRESENT ILLNESS', undatedRanges);
  if (hpiEnd !== -1) return hpiEnd;
  return text.length;
}

function createDateSectionEdit(text, edit, sourceDate, section, state, undatedRanges) {
  const key = sourceDate.key;
  let group = state.createdDates.get(key);
  if (!group) {
    group = {
      index: findDateSectionCreateIndex(text, undatedRanges),
      wroteDate: false,
      sections: new Set(),
    };
    state.createdDates.set(key, group);
  }

  const label = sectionDisplay(section);
  let insert = '';
  if (!group.wroteDate) {
    insert += insertionPrefix(text, group.index);
    insert += sourceDate.display;
    group.wroteDate = true;
  }
  insert += '\n';
  if (label && !group.sections.has(label)) {
    insert += `${label}\n`;
    group.sections.add(label);
  }
  insert += edit.newText;
  insert += insertionSuffix(text, group.index);
  return {
    ...edit,
    located: true,
    placement: 'new-date-section',
    index: group.index,
    matchLen: 0,
    insert,
  };
}

function withPlacement(edit, placement, index, matchLen, insert) {
  return { ...edit, located: true, placement, index, matchLen, insert };
}

function sourceForEdit(edit, selectedByKey) {
  const source = edit.source || {};
  const meta = selectedByKey.get(`${source.type}:${source.id}`);
  return meta ? { ...source, ...meta, label: source.label || meta.label } : source;
}

// Annotate each edit with { located, placement, index, matchLen, insert }.
// `located` is always true now (append/new section is the ultimate fallback).
// Find the body region of a section heading in the doc. Returns the start
// position of the body (right after the heading line + its trailing newline)
// and the end position (right before the next heading line, or end of doc).
//
// Used by locateSectionEdits to do section_rewrite without touching the
// heading itself. This is the structural fix for the per-line apply path's
// off-by-N heading fragmentation bug: by snapping to whole-line boundaries
// instead of character offsets within heading words, we cannot ever split
// "PAST MEDICAL HISTORY" into "PAST MEDICAL HISTO" + content + "RY".
function findSectionBodyRange(text, sectionName, ranges = null) {
  const target = normalizeSectionName(sectionName);
  if (!target) return null;
  const { lines, offsets } = lineInfo(text);
  const allowed = (i) => !ranges || inRanges(offsets[i], ranges);
  let hi = -1;
  for (let i = 0; i < lines.length; i += 1) {
    if (!allowed(i)) continue;
    const t = normalizeSectionName(lines[i]);
    if (!t) continue;
    if (t === target || t.startsWith(`${target} `) || target.startsWith(t)) {
      hi = i;
      break;
    }
  }
  if (hi === -1) {
    for (let i = 0; i < lines.length; i += 1) {
      if (!allowed(i)) continue;
      if (normalizeSectionName(lines[i]).includes(target)) { hi = i; break; }
    }
  }
  if (hi === -1) return null;

  const headingStart = offsets[hi];
  const headingEnd = headingStart + lines[hi].length;
  // bodyStart is right after heading's trailing newline. If hi is the last
  // line of the doc (no trailing newline) bodyStart == headingEnd.
  const bodyStart = hi + 1 < lines.length ? offsets[hi + 1] : headingEnd;

  // Walk to the next heading (or date heading) to find body end.
  let nextHi = lines.length;
  for (let i = hi + 1; i < lines.length; i += 1) {
    if (isHeadingLine(lines[i]) || isDateHeadingLine(lines[i])) {
      nextHi = i;
      break;
    }
  }
  const bodyEnd = nextHi < lines.length ? offsets[nextHi] : text.length;
  return { headingStart, headingEnd, bodyStart, bodyEnd };
}

// Step B: section-keyed apply path. Counterpart to locateEdits for the new
// `sections` field returned by backend Step A. Each section_rewrite edit
// replaces the BODY of its named section in place (between the heading line
// and the next heading), leaving the heading itself untouched.
//
// section_drop edits are no-ops here — the frontend keeps the old content
// when the model dropped a section the user might still want. orphan_add
// edits append at end of doc as a fallback when the model invented a new
// section that doesn't exist in the live doc.
export function locateSectionEdits(text, sectionEdits, options = {}) {
  const docText = String(text || '');
  const selectedByKey = new Map((options.selectedItems || []).map((i) => [`${i.type}:${i.id}`, i]));

  return (sectionEdits || []).map((rawEdit) => {
    const source = sourceForEdit(rawEdit, selectedByKey);
    const edit = { ...rawEdit, source };

    if (edit.kind === 'section_drop') {
      // Skip — keep the existing body. Mark as not-applicable so the UI
      // can render but composeOps drops it.
      return withPlacement(edit, 'noop', docText.length, 0, '');
    }

    if (edit.kind === 'orphan_add') {
      // Model invented a section not present in the live doc. Append at end
      // with the heading included so the body is anchored under a header.
      return withPlacement(
        edit,
        'append',
        docText.length,
        0,
        docText.endsWith('\n') ? `\n${edit.newText}\n` : `\n\n${edit.newText}\n`,
      );
    }

    // Default + the canonical case: section_rewrite.
    const range = findSectionBodyRange(docText, edit.section || edit.anchor);
    if (!range) {
      // Section not found in the live doc — append the new section at the end.
      const heading = (edit.section || edit.anchor || '').trim();
      const newText = (edit.newText || '').trim();
      const insertBlock = heading
        ? `\n\n${heading}\n${newText}\n`
        : `\n\n${newText}\n`;
      return withPlacement(edit, 'append', docText.length, 0, insertBlock);
    }

    const matchLen = range.bodyEnd - range.bodyStart;
    const newBody = (edit.newText || '').replace(/^\n+|\n+$/g, '');
    // Insert MUST start with `\n` so Quill recognizes a fresh block break
    // instead of merging the body INTO the heading block. Matches the per-
    // line locateEdits convention (`\n${newText}` for 'section' placements).
    // Without this, small bodies (e.g. PMH/PSH "___") get absorbed into the
    // heading block — Quill collapses the formatting boundary — and the
    // heading visually fragments ("PAST MEDICAL HISTOR[content]Y").
    const insert = newBody ? `\n${newBody}\n` : '\n';
    return withPlacement(edit, 'section', range.bodyStart, matchLen, insert);
  });
}

export function locateEdits(text, edits, options = {}) {
  const docText = String(text || '');
  const dateSections = getDateSections(docText);
  const undatedRanges = getUndatedRanges(docText, dateSections);
  const selectedByKey = new Map((options.selectedItems || []).map((i) => [`${i.type}:${i.id}`, i]));
  const state = { createdDates: new Map() };

  return (edits || []).map((rawEdit) => {
    const source = sourceForEdit(rawEdit, selectedByKey);
    const sourceDate = parseSourceDate(rawEdit.sourceDate || source.date || rawEdit.date);
    const routeToRhm = shouldRouteToRhm(rawEdit, options.documentType);
    const leadingSection = leadingPlacementSection(rawEdit.newText, sourceDate);
    const targetSection = routeToRhm
      ? 'RHM'
      : chooseTargetSection(docText, rawEdit.section, leadingSection, undatedRanges);
    const edit = {
      ...rawEdit,
      section: targetSection || rawEdit.section,
      newText: routeToRhm
        ? formatRhmActionItems(stripLeadingPlacementContext(rawEdit.newText, sourceDate, targetSection))
        : stripLeadingPlacementContext(rawEdit.newText, sourceDate, targetSection),
      source,
    };

    const inSameDate = (idx) => {
      if (!sourceDate) return false;
      const section = dateSectionAtIndex(dateSections, idx);
      return !!section && datesMatch(sourceDate, section.date);
    };
    const inUndated = (idx) => inRanges(idx, undatedRanges);
    const notWrongDate = (idx) => !sourceDate || inSameDate(idx) || inUndated(idx);
    const sameDateRanges = sourceDate
      ? dateSections.filter((s) => datesMatch(sourceDate, s.date)).map((s) => ({ start: s.start, end: s.end }))
      : [];

    if (edit.kind === 'correction' && edit.oldText) {
      const idx = findIndex(docText, edit.oldText, notWrongDate);
      if (idx !== -1) {
        return withPlacement(edit, 'anchor', idx, edit.oldText.length, edit.newText);
      }
    }

    if (edit.kind === 'addition' && edit.anchor) {
      if (sourceDate) {
        const sameDateAnchor = findIndex(docText, edit.anchor, inSameDate);
        if (sameDateAnchor !== -1) {
          return withPlacement(edit, 'anchor', sameDateAnchor + edit.anchor.length, 0, ` ${edit.newText}`);
        }
      } else {
        const aIdx = findIndex(docText, edit.anchor);
        if (aIdx !== -1) {
          return withPlacement(edit, 'anchor', aIdx + edit.anchor.length, 0, ` ${edit.newText}`);
        }
      }
    }

    if (sourceDate) {
      if (routeToRhm) {
        const canonicalRhmIdx = findCanonicalRhmInsert(docText, undatedRanges);
        if (canonicalRhmIdx !== -1) {
          return withPlacement(edit, 'section', canonicalRhmIdx, 0, `\n${edit.newText}`);
        }
      }

      const undatedSectionIdx = findSectionInsert(docText, targetSection, undatedRanges);
      if (undatedSectionIdx !== -1) {
        return withPlacement(edit, 'section', undatedSectionIdx, 0, `\n${edit.newText}`);
      }

      const sameDateSectionIdx = findSectionInsert(docText, targetSection, sameDateRanges);
      if (sameDateSectionIdx !== -1) {
        return withPlacement(edit, 'date-section', sameDateSectionIdx, 0, `\n${edit.newText}`);
      }

      const matchingDate = dateSections.find((s) => datesMatch(sourceDate, s.date));
      if (matchingDate) {
        const insert = routeToRhm
          ? `\n${sectionDisplay('RHM')}\n${edit.newText}`
          : `\n${edit.newText}`;
        return withPlacement(edit, 'date', matchingDate.insertEnd, 0, insert);
      }

      if (edit.kind === 'addition' && edit.anchor) {
        const undatedAnchor = findIndex(docText, edit.anchor, inUndated);
        if (undatedAnchor !== -1) {
          return withPlacement(edit, 'anchor', undatedAnchor + edit.anchor.length, 0, ` ${edit.newText}`);
        }
      }

      return createDateSectionEdit(docText, edit, sourceDate, targetSection, state, undatedRanges);
    }

    if (routeToRhm) {
      const rhmIdx = findCanonicalRhmInsert(docText, undatedRanges);
      if (rhmIdx !== -1) {
        return withPlacement(edit, 'section', rhmIdx, 0, `\n${edit.newText}`);
      }
      return withPlacement(edit, 'section', docText.length, 0, createSectionInsert(docText, 'RHM', edit.newText));
    }

    const sectionIdx = findSectionInsert(docText, targetSection);
    if (sectionIdx !== -1) {
      return withPlacement(edit, 'section', sectionIdx, 0, `\n${edit.newText}`);
    }

    if (!docText.trim() && targetSection) {
      return withPlacement(edit, 'section', 0, 0, createSectionInsert(docText, targetSection, edit.newText));
    }

    return withPlacement(edit, 'append', docText.length, 0, appendInsert(docText, edit.newText));
  });
}

// Expand markdown bold (`**text**`) inside an insert string into a sequence
// of Quill ops with the bold attribute applied. The model emits markdown
// bold for ICD problem lines ("**1. Obstructive sleep apnea, ...**") and
// occasionally for emphasized findings — Quill doesn't parse markdown, so
// without this expansion the asterisks render as literal text. Newlines do
// not cross a bold range (the regex requires non-* non-\n characters between
// the `**` markers), so multi-line bold blocks degrade gracefully to plain
// text instead of swallowing the whole rest of the section.
//
// Special case — numbered ICD problem lines ("**1. Chest pain, unspecified**"):
// instead of a bold text op, emit an `icd-problem` BlockEmbed op so the
// rendered line is interactive (click pencil to search ICD-10). The blot
// stores `{problemNumber, displayName, icd10Code}` and round-trips through
// the Quill delta + Yjs stack the same way as the existing custom blots.
export function expandMarkdownInsertToOps(text) {
  const out = [];
  if (text == null || text === '') return out;
  const str = String(text);
  const re = /\*\*([^*\n][^*\n]*?)\*\*/g;
  let last = 0;
  let m;
  while ((m = re.exec(str)) !== null) {
    if (m.index > last) {
      out.push({ insert: str.slice(last, m.index) });
    }
    out.push({ insert: m[1], attributes: { bold: true } });
    last = m.index + m[0].length;
  }
  if (last < str.length) {
    out.push({ insert: str.slice(last) });
  }
  // Coalesce: an empty regex tail or a single all-plain string produces one
  // op. If the loop never matched, return a single insert op with the raw
  // text so downstream behavior is unchanged for plain inserts.
  if (out.length === 0) return [{ insert: str }];
  return out;
}

export function composeOps(text, locatedEdits, approvals) {
  const approved = (locatedEdits || [])
    .filter((e) => approvals[e.id] === 'approved' && (e.matchLen > 0 || e.insert))
    .slice()
    .sort((a, b) => a.index - b.index);

  const ops = [];
  let cursor = 0;
  for (const e of approved) {
    if (e.index < cursor) continue;
    if (e.index > cursor) { ops.push({ retain: e.index - cursor }); cursor = e.index; }
    if (e.matchLen > 0) { ops.push({ delete: e.matchLen }); cursor += e.matchLen; }
    if (e.insert) {
      for (const op of expandMarkdownInsertToOps(e.insert)) {
        ops.push(op);
      }
    }
  }
  return ops;
}

export function buildPreviewSegments(text, locatedEdits, approvals) {
  const placed = (locatedEdits || []).filter((e) => e.located).slice().sort((a, b) => a.index - b.index);
  const segments = [];
  let cursor = 0;
  for (const e of placed) {
    if (e.index < cursor) continue;
    if (e.index > cursor) segments.push({ kind: 'context', text: text.slice(cursor, e.index) });
    segments.push({ kind: 'edit', edit: e, status: approvals[e.id] || 'pending' });
    cursor = e.index + e.matchLen;
  }
  if (cursor < text.length) segments.push({ kind: 'context', text: text.slice(cursor) });
  return segments;
}
